import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Shield, Clock, Hash, Link as LinkIcon, Loader2 } from 'lucide-react';

interface VerificacionInfo {
  valid: boolean;
  numeroFactura: string;
  fecha: string;
  total: number;
  estado: string;
  verifactu: {
    numeroRegistro: number;
    fechaRegistro: string;
    hash: string;
    hashAnterior: string;
    posicionCadena: string;
  };
  verificacion: {
    integridadHash: boolean;
    hashCoincide: boolean;
    timestamp: string;
  };
  message: string;
}

export default function VerificarFactura() {
  const { userId, numeroRegistro, hash } = useParams<{
    userId: string;
    numeroRegistro: string;
    hash: string;
  }>();

  const [info, setInfo] = useState<VerificacionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !numeroRegistro || !hash) {
      setError('Parámetros de verificación incompletos');
      setIsLoading(false);
      return;
    }

    verificarFactura();
  }, [userId, numeroRegistro, hash]);

  const verificarFactura = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/verifactu/verify/${userId}/${numeroRegistro}/${hash}`
      );

      if (!response.ok) {
        throw new Error('No se pudo verificar la factura');
      }

      const data = await response.json();
      setInfo(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al verificar la factura');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verificando factura...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-destructive" />
              <CardTitle>Error de Verificación</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTitle>No se pudo verificar la factura</AlertTitle>
              <AlertDescription>{error || 'La factura no existe o los datos de verificación son incorrectos'}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isValid = info.valid && info.verificacion.integridadHash && info.verificacion.hashCoincide;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header con estado de verificación */}
        <Card className={isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardHeader>
            <div className="flex items-center gap-3">
              {isValid ? (
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              ) : (
                <XCircle className="h-10 w-10 text-red-600" />
              )}
              <div className="flex-1">
                <CardTitle className={isValid ? 'text-green-900' : 'text-red-900'}>
                  {isValid ? 'Factura Verificada' : 'Verificación Fallida'}
                </CardTitle>
                <CardDescription className={isValid ? 'text-green-700' : 'text-red-700'}>
                  {info.message}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Información de la factura */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Datos de la Factura
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Número de Factura</p>
                <p className="text-lg font-semibold">{info.numeroFactura}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Estado</p>
                <Badge variant={info.estado === 'Emitida' ? 'default' : 'secondary'}>
                  {info.estado}
                </Badge>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Fecha de Emisión</p>
                <p className="font-mono text-sm">
                  {new Date(info.fecha).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Importe Total</p>
                <p className="text-lg font-semibold text-primary">
                  {info.total.toLocaleString('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información Verifactu */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Datos de Verifactu
            </CardTitle>
            <CardDescription>
              Sistema de integridad y trazabilidad según normativa española
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Número de Registro</p>
                <p className="font-mono text-sm font-semibold">#{info.verifactu.numeroRegistro}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Fecha de Registro</p>
                <p className="font-mono text-sm">
                  {new Date(info.verifactu.fechaRegistro).toLocaleString('es-ES')}
                </p>
              </div>
              <div className="space-y-1 md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground">Posición en Cadena</p>
                <p className="text-sm">{info.verifactu.posicionCadena}</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Hash de Integridad (SHA-256)</p>
              <code className="block text-xs bg-muted p-3 rounded font-mono break-all">
                {info.verifactu.hash}
              </code>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Hash Anterior (Encadenamiento)
              </p>
              <code className="block text-xs bg-muted p-3 rounded font-mono break-all">
                {info.verifactu.hashAnterior}
              </code>
            </div>
          </CardContent>
        </Card>

        {/* Resultados de verificación */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Resultados de la Verificación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
              <span className="text-sm font-medium">Integridad del Hash</span>
              {info.verificacion.integridadHash ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
              <span className="text-sm font-medium">Hash Coincide</span>
              {info.verificacion.hashCoincide ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
            </div>

            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground">
                Verificación realizada el:{' '}
                {new Date(info.verificacion.timestamp).toLocaleString('es-ES', {
                  dateStyle: 'long',
                  timeStyle: 'medium',
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer informativo */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900">
                  Sistema Verifactu - Agencia Tributaria Española
                </p>
                <p className="text-xs text-blue-700">
                  Esta factura cumple con la normativa española de facturación electrónica
                  (Orden HAC/1177/2024). La verificación confirma que los datos no han sido
                  alterados desde su emisión y están correctamente encadenados en el sistema
                  de integridad.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
